import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { usePrefersReducedMotion } from './use-prefers-reduced-motion.js';
import { emberSlots, flameConstruction, flameCoreConstruction, flameCoreTilt, flameEmber, flameEmbers, flameGeometry, flameTangentGuides, restingEmbers } from './flame-artwork.js';
// Let CSS blend the actual theme tokens, avoiding duplicated RGB values and
// preserving theme changes while the animation is running.
const emberFill = (orange) => orange === 0 ? 'var(--color-flame-core)'
    : orange === 1 ? 'var(--color-flame)'
        : `color-mix(in srgb, var(--color-flame-core) ${((1 - orange) * 100).toFixed(1)}%, var(--color-flame))`;
// Cache painted attributes for this effect's lifetime. Many values (phase,
// visibility, colour endpoints and disc x coordinates) stay unchanged while
// the paths move. Avoid invalidating SVG style/layout for identical writes.
// Recreate on effect restart so React prop changes cannot leave stale values.
function attributeWriter() {
    const cache = new WeakMap();
    return (node, name, value) => {
        if (!node)
            return;
        const text = String(value);
        let attributes = cache.get(node);
        if (!attributes) {
            attributes = new Map();
            cache.set(node, attributes);
        }
        if (attributes.get(name) === text)
            return;
        attributes.set(name, text);
        node.setAttribute(name, text);
    };
}
const emptyEmbers = Array.from({ length: emberSlots }, () => null);
const duration = 600;
const shapeDuration = 700;
const circleDuration = 900;
const clamp = (value) => Math.max(0, Math.min(1, value));
const smootherstep = (value) => value ** 3 * (10 + value * (-15 + 6 * value));
// Zero velocity and acceleration at the ends and peak avoid a sudden kick
// when a one-shot accent meets the continuing flame motion.
const pulse = (progress, start, peak, end) => smootherstep(clamp(progress < peak ? (progress - start) / (peak - start) : (end - progress) / (end - peak)));
// A single damped wave crosses neutral scale without pausing between two
// separate pulses. The sine-squared window gives zero speed and acceleration
// at entry/exit; decay reduces the rebound after the initial compression.
// Start shrinking during retraction rather than waiting for the circle.
// The time warp puts compression before the merge and the rebound after it,
// while preserving a gentle start and a continuous, damped return.
const circleRebound = (progress) => {
    const q = clamp(progress) ** 1.4;
    return q === 0 || q === 1 ? 0
        : -0.24 * Math.exp(-2.3 * q) * Math.sin(2 * Math.PI * q) * Math.sin(Math.PI * q) ** 2;
};
// One stronger drop and stretch follows the outer tongue's lift, then returns
// directly to rest. A positive-only quintic pulse avoids the extra upward
// recoil of a damped wave. Position and tip share this impulse, with zero
// speed/acceleration at the ends and peak: +0.15R at 518 ms, zero at 700 ms.
const coreOvershoot = (progress) => 0.15 * pulse(progress, 0.42, 0.74, 1);
const intensityValue = (value) => Number.isFinite(value) ? clamp(value) : 1;
const particleVisibility = (intensity) => {
    const value = clamp((intensity - 0.65) / 0.35);
    return value * value * (3 - 2 * value);
};
// Use the nominal bowl diameter as a fixed reference: changing its width with
// the bowl would mix scale variation into the tilt reading. Keep both guides
// outside the rotated group. The value sits below the live arrow tip.
const tiltArrow = (direction, fraction = 1) => {
    const start = 12;
    const end = start + direction * 7 * fraction;
    const head = Math.min(0.55, Math.abs(end - start));
    return `M${start} 25.5 H${end} M${end - direction * head} ${25.5 - head} L${end} 25.5 L${end - direction * head} ${25.5 + head}`;
};
// Keep the Text node alive: assigning textContent replaces it and fires
// child-list observers. Storybook's highlighter then scans computed styles
// across the whole page on every frame, even though only a number changed.
const setReadout = (node, value) => {
    const text = node?.firstChild;
    if (text && text.nodeValue !== value)
        text.nodeValue = value;
};
const tiltText = (rotation) => `${rotation > 0.05 ? '+' : ''}${(Math.abs(rotation) < 0.05 ? 0 : rotation).toFixed(1)}°`;
/** Decorative fire with a planted base, independent tips, and a warm inner core.
 * This is deliberately drawn artwork: the owner requested freedom from the icon
 * library so its motion can read as fire, rather than a distorted static glyph.
 * No animation player is required; circle geometry drives the main silhouette.
 */
export function Flame({ state = 'burning', size = 24, animated = true, intensity = 1, particles = true, playbackRate = 1, idleSpeed = 1, onShapeChange, showConstruction = false, label, className, style }) {
    const targetIntensity = intensityValue(intensity);
    // React keeps the mount pose stable; target changes are painted from the
    // current animated value, so reversing cannot flash the opposite endpoint.
    const [initialIntensity] = useState(targetIntensity);
    const restEmbers = useMemo(() => !particles || showConstruction ? [] : restingEmbers(size), [size, showConstruction, particles]);
    const restConstruction = useMemo(() => flameConstruction(0, initialIntensity), [initialIntensity]);
    const restCore = useMemo(() => flameCoreConstruction(restConstruction, initialIntensity), [restConstruction, initialIntensity]);
    const restCoreRotation = flameCoreTilt(0, initialIntensity) - restConstruction.rotation;
    const clock = useRef({ time: 0, state, value: initialIntensity, from: initialIntensity,
        target: initialIntensity, coreValue: initialIntensity, fromCoreValue: initialIntensity,
        elapsed: shapeDuration, duration: shapeDuration,
        lift: 0, scale: 1, coreStretch: 0, fromLift: 0, fromScale: 1, fromCoreStretch: 0 });
    const bursts = useRef([]);
    const shapeListener = useRef(onShapeChange);
    const reportedShape = useRef(null);
    // A new listener must not restart the animation clock. Notify only geometric
    // phase boundaries, so composition can wait for a real settled circle even
    // when the flame was paused offscreen or a transition was interrupted.
    useEffect(() => { shapeListener.current = onShapeChange; }, [onShapeChange]);
    const id = useId();
    const outerId = `flame-outer-${id}`;
    const clipId = `flame-core-clip-${id}`;
    const svg = useRef(null);
    const artwork = useRef(null);
    const outer = useRef(null);
    const core = useRef(null);
    const tangentNodes = useRef([]);
    const constructionNodes = useRef([]);
    const coreDiscNodes = useRef([]);
    const coreTangentNodes = useRef([]);
    const coreGuides = useRef(null);
    const coreOrigin = useRef(null);
    const emberNodes = useRef([]);
    const tiltArrows = useRef([]);
    const tiltLabel = useRef(null);
    const scaleMarker = useRef(null);
    const scaleLabel = useRef(null);
    const unlit = useRef(null);
    const outline = useRef(null);
    const reduced = usePrefersReducedMotion();
    const moving = animated && !reduced;
    const litEndpoint = state === 'burning' || state === 'igniting';
    const initialHeat = moving ? (state === 'burning' || state === 'extinguishing' ? 1 : 0) : Number(litEndpoint);
    useEffect(() => {
        const element = svg.current;
        if (!element)
            return;
        const current = clock.current;
        if (current.state !== state) {
            current.state = state;
            current.time = 0;
            bursts.current = [];
        }
        if (current.target !== targetIntensity) {
            current.from = current.value;
            current.fromCoreValue = current.coreValue;
            current.fromLift = current.lift;
            current.fromScale = current.scale;
            current.fromCoreStretch = current.coreStretch;
            current.target = targetIntensity;
            current.duration = current.target < current.from ? circleDuration : shapeDuration;
            // A short upward impulse meets the visual emergence; existing particles
            // keep the resulting momentum even if this transition is interrupted.
            if (moving && litEndpoint && current.target > current.from)
                bursts.current.push({ start: current.time + 0.25, strength: current.target - current.from });
            current.elapsed = 0;
        }
        let frame = 0;
        let elapsed = 0;
        let previous = null;
        let inView = true;
        let done = false;
        const rate = Number.isFinite(playbackRate) ? Math.max(0.1, playbackRate) : 1;
        const idleRate = Number.isFinite(idleSpeed) ? Math.max(0.1, Math.min(1, idleSpeed)) : 1;
        // Reapply on effect restart: the DOM may still carry an animated tilt
        // when reduced motion switches to the unchanged React resting pose.
        let lastRotation = null;
        let lastCoreRotation = null;
        let lastHeat = -1;
        let lastCoreHeat = -1;
        let lastOutline = null;
        let lastCore = null;
        const write = attributeWriter();
        const paint = (heat, time, level, still = false) => {
            const coreLevel = still ? level : current.coreValue;
            const motion = flameGeometry(still ? 0 : time, level, still ? undefined : { tongueLift: current.lift, bodyScale: current.scale,
                coreStretch: current.coreStretch, coreIntensity: coreLevel });
            const rotation = motion.rotation;
            write(element, 'data-intensity', level);
            const shape = current.elapsed < current.duration ? 'transition' : level === 0 ? 'circle' : level === 1 ? 'flame' : 'transition';
            write(element, 'data-shape', shape);
            if (reportedShape.current !== shape) {
                reportedShape.current = shape;
                shapeListener.current?.(shape);
            }
            if (rotation !== lastRotation) {
                write(artwork.current, 'transform', `rotate(${rotation} 12 15)`);
                lastRotation = rotation;
            }
            // Cancel the live parent angle before applying the delayed angle. The
            // core and its construction rotate together about the outer bowl origin.
            const coreRotation = motion.coreRotation - rotation;
            if (coreRotation !== lastCoreRotation) {
                const transform = `rotate(${coreRotation} 12 15)`;
                write(core.current, 'transform', transform);
                write(coreGuides.current, 'transform', transform);
                lastCoreRotation = coreRotation;
            }
            const nextOutline = motion.outline;
            if (nextOutline !== lastOutline) {
                write(outline.current, 'd', nextOutline);
                lastOutline = nextOutline;
            }
            if (showConstruction) {
                const construction = motion;
                const guides = flameTangentGuides(construction);
                tangentNodes.current.forEach((node, index) => {
                    write(node, 'd', guides[index]?.tangent ?? '');
                    write(node, 'opacity', guides[index] ? '1' : '0');
                });
                motion.coreDiscs.forEach((disc, index) => {
                    const node = coreDiscNodes.current[index];
                    write(node, 'cx', String(disc.x));
                    write(node, 'cy', String(disc.y));
                    write(node, 'r', String(disc.radius));
                });
                write(coreOrigin.current, 'cx', String(motion.coreDiscs[0].x));
                write(coreOrigin.current, 'cy', String(motion.coreDiscs[0].y));
                coreTangentNodes.current.forEach((node, index) => {
                    const tangent = motion.coreTangents[index];
                    write(node, 'd', tangent ? `M${tangent.body.x} ${tangent.body.y} L${tangent.tip.x} ${tangent.tip.y}` : '');
                });
                write(coreGuides.current, 'opacity', String(clamp((heat - 0.2) / 0.8) * coreLevel));
                const radius = construction.discs[0].radius;
                const fraction = Math.abs(rotation) / 15;
                tiltArrows.current.forEach((node, index) => {
                    const direction = index === 0 ? -1 : 1;
                    write(node, 'd', tiltArrow(direction, fraction));
                    write(node, 'opacity', rotation * direction > 0.05 ? '1' : '0');
                });
                if (tiltLabel.current) {
                    write(tiltLabel.current, 'x', String(12 + rotation / 15 * 7));
                    setReadout(tiltLabel.current, tiltText(rotation));
                }
                const scale = radius / 7;
                // Fixed 90–110% scale: the live marker moves, not the reference rail.
                const markerY = 22 - clamp((scale - 0.9) / 0.2) * 14;
                write(scaleMarker.current, 'cy', String(markerY));
                write(scaleLabel.current, 'y', String(markerY + 0.35));
                setReadout(scaleLabel.current, `${(scale * 100).toFixed(1)}%`);
                construction.discs.forEach((disc, index) => {
                    const node = constructionNodes.current[index];
                    write(node, 'cx', String(disc.x));
                    write(node, 'cy', String(disc.y));
                    write(node, 'r', String(disc.radius));
                    write(node, 'opacity', String(disc.opacity ?? 1));
                });
            }
            const nextCore = motion.core;
            if (nextCore !== lastCore) {
                write(core.current, 'd', nextCore);
                lastCore = nextCore;
            }
            // Construction has no particles. Below 0.65 they are fully transparent;
            // advance their deterministic clock without evaluating unseen flights.
            const visibility = particleVisibility(level);
            const embers = !particles || showConstruction ? [] : visibility === 0 ? emptyEmbers
                : still ? restEmbers : flameEmbers(time, size, rotation, bursts.current);
            embers.forEach((ember, index) => {
                const node = emberNodes.current[index];
                if (!node)
                    return;
                write(node, 'opacity', String((ember?.opacity ?? 0) * visibility));
                if (!ember)
                    return;
                write(node, 'transform', ember.transform);
                write(node, 'fill', emberFill(ember.orange));
                if (node.dataset.emberId !== String(ember.id)) {
                    node.dataset.emberId = String(ember.id);
                    node.dataset.shape = ember.shape;
                    write(node.children[0], 'opacity', ember.shape === 'diamond' ? '1' : '0');
                    write(node.children[1], 'opacity', ember.shape === 'dot' ? '1' : '0');
                }
            });
            const coreHeat = clamp((heat - 0.2) / 0.8) * coreLevel;
            if (coreHeat !== lastCoreHeat) {
                write(core.current, 'opacity', String(coreHeat));
                lastCoreHeat = coreHeat;
            }
            if (heat !== lastHeat) {
                lastHeat = heat;
                write(outer.current, 'opacity', String(heat));
                write(unlit.current, 'opacity', String(1 - heat));
            }
        };
        const endpoint = () => {
            current.value = current.target;
            current.coreValue = current.target;
            current.elapsed = current.duration;
            current.lift = 0;
            current.scale = 1;
            current.coreStretch = 0;
            bursts.current = [];
            paint(Number(litEndpoint), 0, current.target, true);
            write(element, 'data-phase', litEndpoint ? 'burning' : 'unlit');
        };
        if (!moving || state === 'unlit' || (current.value === 0 && current.target === 0 && current.elapsed === current.duration)) {
            endpoint();
            return;
        }
        const tick = (now) => {
            const delta = previous === null ? 0 : Math.min(now - previous, 50) * rate;
            elapsed += delta;
            // Slow only the continuous clock, keeping the established transition
            // choreography and ignition impulse on their normal 700/900 ms timing.
            // Advance the existing phase instead of multiplying elapsed time: changing
            // idleSpeed must never jump the silhouette or airborne particle positions.
            const transitioning = current.elapsed < current.duration || state !== 'burning';
            current.time += delta / 1000 * (transitioning ? 1 : idleRate);
            // No particle can outlive a burst's 0.45 s force plus its 1.45 s life.
            if (bursts.current[0] && current.time - bursts.current[0].start >= 2)
                bursts.current = bursts.current.filter(burst => current.time - burst.start < 2);
            current.elapsed = Math.min(current.duration, current.elapsed + delta);
            const shapeProgress = current.elapsed / current.duration;
            const closing = current.target < current.from;
            // Close the contour by 55% so the final rebound belongs to the circle.
            // Opening uses the full duration and lifts the tips past their ordinary
            // targets before settling back, while preserving their overlap timing.
            const shapeEase = smootherstep(clamp(shapeProgress / (closing ? 0.55 : 1)));
            const settleEase = smootherstep(shapeProgress);
            const amount = Math.abs(current.target - current.from);
            current.value = shapeEase === 1 ? current.target : current.from + (current.target - current.from) * shapeEase;
            // Let the outer shape lead. During opening, reveal, circle-to-tip geometry,
            // centre travel and tilt share a delayed 210–630 ms envelope. Delaying
            // only the bounce left a visible core floating inside the circle first.
            // Closing retains its existing timing; reversals begin at the painted
            // core value so neither opacity nor geometry jumps.
            const coreEase = closing ? shapeEase : smootherstep(clamp((shapeProgress - 0.30) / 0.60));
            current.coreValue = coreEase === 1 ? current.target
                : current.fromCoreValue + (current.target - current.fromCoreValue) * coreEase;
            // Carry the current accents through a reversal instead of resetting the
            // geometry. Short interrupted transitions get proportionally less force.
            current.lift = current.fromLift * (1 - settleEase)
                + (closing ? 0 : amount * 1.8 * pulse(shapeProgress, 0.3, 0.72, 1));
            current.scale = 1 + (current.fromScale - 1) * (1 - settleEase)
                + (closing ? amount * circleRebound(shapeProgress) : 0);
            // Closing adds no core bounce; only carry an interrupted opening's
            // current displacement so changing direction cannot snap the geometry.
            current.coreStretch = current.fromCoreStretch * (1 - settleEase)
                + (closing ? 0 : amount * coreOvershoot(shapeProgress));
            previous = now;
            const progress = clamp(elapsed / duration);
            const heat = state === 'igniting' ? 1 - (1 - progress) ** 2
                : state === 'extinguishing' ? (1 - progress) ** 2 : 1;
            // Keep the gentle main flicker while the lower bowl stays registered.
            paint(heat, current.time, current.value);
            write(element, 'data-phase', state);
            if (state !== 'burning' && progress === 1 && shapeProgress === 1) {
                done = true;
                endpoint();
            }
            else if (current.value === 0 && current.target === 0 && shapeProgress === 1) {
                // Finish the radius rebound before stopping at the exact circle.
                bursts.current = [];
                done = true;
            }
            else
                frame = requestAnimationFrame(tick);
        };
        paint(initialHeat, current.time, current.value);
        const sync = () => {
            cancelAnimationFrame(frame);
            previous = null;
            if (!done && inView && !document.hidden)
                frame = requestAnimationFrame(tick);
        };
        const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
            inView = entries[0]?.isIntersecting ?? true;
            sync();
        }) : null;
        observer?.observe(element);
        document.addEventListener('visibilitychange', sync);
        sync();
        return () => {
            cancelAnimationFrame(frame);
            observer?.disconnect();
            document.removeEventListener('visibilitychange', sync);
        };
    }, [state, moving, litEndpoint, playbackRate, idleSpeed, size, showConstruction, targetIntensity, initialHeat, restEmbers, particles]);
    return (_jsxs("svg", { ref: svg, className: ['sc-flame', className].filter(Boolean).join(' '), width: size, height: size, viewBox: "0 0 24 24", style: style, "data-intensity": initialIntensity, "data-shape": initialIntensity === 0 ? 'circle' : initialIntensity === 1 ? 'flame' : 'transition', "data-state": state, "data-phase": moving ? state : litEndpoint ? 'burning' : 'unlit', "aria-hidden": label ? undefined : true, role: label ? 'img' : undefined, "aria-label": label, focusable: "false", children: [_jsxs("g", { ref: artwork, "data-flame-part": "artwork", transform: `rotate(${restConstruction.rotation} 12 15)`, children: [_jsxs("defs", { children: [_jsx("path", { id: `${outerId}-outline`, ref: outline, d: restConstruction.outline }), _jsx("clipPath", { id: clipId, clipPathUnits: "userSpaceOnUse", children: _jsx("use", { href: `#${outerId}-outline` }) })] }), _jsxs("g", { id: outerId, ref: outer, fill: "var(--color-flame)", opacity: initialHeat, children: [_jsx("use", { "data-flame-part": "base", href: `#${outerId}-outline`, fillOpacity: showConstruction ? 0.05 : undefined }), particles && !showConstruction && Array.from({ length: emberSlots }, (_, index) => {
                                const ember = restEmbers[index];
                                return _jsxs("g", { ref: node => { emberNodes.current[index] = node; }, "data-flame-part": "ember", "data-ember-id": ember?.id, "data-shape": ember?.shape, fill: emberFill(ember?.orange ?? 1), transform: ember?.transform, opacity: (ember?.opacity ?? 0) * particleVisibility(initialIntensity), children: [_jsx("path", { "data-ember-shape": "diamond", d: flameEmber, opacity: ember?.shape === 'diamond' ? 1 : 0 }), _jsx("circle", { "data-ember-shape": "dot", r: 0.38, opacity: ember?.shape === 'dot' ? 1 : 0 })] }, index);
                            })] }), _jsx("g", { clipPath: `url(#${clipId})`, children: _jsx("path", { "data-flame-part": "core", ref: core, d: restCore.outline, transform: `rotate(${restCoreRotation} 12 15)`, fill: showConstruction ? 'none' : 'var(--color-flame-core)', opacity: initialHeat * initialIntensity }) }), _jsx("use", { ref: unlit, href: `#${outerId}-outline`, fill: "none", stroke: "var(--color-text-secondary)", strokeWidth: "1.4", strokeLinejoin: "round", opacity: 1 - initialHeat }), showConstruction && _jsxs("g", { "data-flame-part": "construction", fill: "none", stroke: "var(--color-flame)", pointerEvents: "none", "aria-hidden": "true", children: [_jsxs("g", { "data-flame-part": "rotation-origin", strokeWidth: 1.25, children: [_jsx("circle", { cx: 12, cy: 15, r: 0.4, vectorEffect: "non-scaling-stroke" }), _jsx("path", { d: "M10.7 15 H13.3 M12 13.7 V16.3", vectorEffect: "non-scaling-stroke" })] }), restConstruction.discs.map((disc, index) => _jsx("circle", { "data-flame-disc": ['body', 'main-tip', 'left-tip', 'right-tip', 'left-join', 'right-join'][index], ref: node => { constructionNodes.current[index] = node; }, cx: disc.x, cy: disc.y, r: disc.radius, opacity: disc.opacity ?? 1, strokeWidth: 1.25, vectorEffect: "non-scaling-stroke" }, index)), _jsxs("g", { ref: coreGuides, "data-flame-part": "core-construction", transform: `rotate(${restCoreRotation} 12 15)`, opacity: initialHeat * initialIntensity, children: [_jsx("circle", { ref: coreOrigin, "data-flame-part": "core-origin", cx: restCore.discs[0].x, cy: restCore.discs[0].y, r: 0.22, fill: "var(--color-flame)", stroke: "none" }), restCore.discs.map((disc, index) => _jsx("circle", { "data-flame-core-disc": index === 0 ? 'body' : 'tip', ref: node => { coreDiscNodes.current[index] = node; }, cx: disc.x, cy: disc.y, r: disc.radius, strokeWidth: 1.25, vectorEffect: "non-scaling-stroke" }, index)), Array.from({ length: 2 }, (_, index) => {
                                        const tangent = restCore.tangents[index];
                                        return _jsx("path", { "data-flame-core-guide": "tangent", ref: node => { coreTangentNodes.current[index] = node; }, d: tangent ? `M${tangent.body.x} ${tangent.body.y} L${tangent.tip.x} ${tangent.tip.y}` : '', strokeWidth: 1.25, vectorEffect: "non-scaling-stroke" }, index);
                                    })] }), Array.from({ length: 6 }, (_, index) => {
                                const guide = flameTangentGuides(restConstruction)[index];
                                return _jsx("path", { ref: node => { tangentNodes.current[index] = node; }, "data-flame-guide": index < 2 ? 'outer-tangent' : 'inner-tangent', d: guide?.tangent ?? '', opacity: guide ? 1 : 0, strokeWidth: 1.25, vectorEffect: "non-scaling-stroke" }, index);
                            })] })] }), showConstruction && _jsxs("g", { "data-flame-part": "motion-indicators", "aria-hidden": "true", pointerEvents: "none", children: [_jsxs("g", { "data-flame-part": "tilt-indicator", "data-max-tilt": "15", fill: "none", stroke: "var(--color-flame)", children: [_jsx("path", { "data-flame-part": "tilt-track", d: "M5 25.5 H19 M5 25.1 V25.9 M12 25.1 V25.9 M19 25.1 V25.9", opacity: 0.35, strokeWidth: 1.25, vectorEffect: "non-scaling-stroke" }), [-1, 1].map((direction, index) => _jsx("path", { "data-flame-part": direction < 0 ? 'tilt-left' : 'tilt-right', ref: node => { tiltArrows.current[index] = node; }, d: tiltArrow(direction, Math.abs(restConstruction.rotation) / 15), opacity: restConstruction.rotation * direction > 0.05 ? 1 : 0, strokeWidth: 2, vectorEffect: "non-scaling-stroke" }, direction)), _jsxs("g", { fontSize: 1.05, fill: "var(--color-text-secondary)", stroke: "none", children: [_jsx("text", { x: 4, y: 25.85, textAnchor: "end", children: "\u221215\u00B0" }), _jsx("text", { x: 20, y: 25.85, children: "+15\u00B0" })] }), _jsx("text", { "data-flame-part": "tilt-label", ref: tiltLabel, x: 12 + restConstruction.rotation / 15 * 7, y: 27.2, textAnchor: "middle", fontSize: 1.15, fill: "var(--color-text-secondary)", stroke: "none", children: tiltText(restConstruction.rotation) })] }), _jsxs("g", { "data-flame-part": "scale-indicator", fill: "none", stroke: "var(--color-flame)", children: [_jsx("path", { d: "M22.5 8 V22 M22.1 8 H22.9 M22.1 15 H22.9 M22.1 22 H22.9", opacity: 0.35, strokeWidth: 1.25, vectorEffect: "non-scaling-stroke" }), _jsx("circle", { "data-flame-part": "scale-marker", ref: scaleMarker, cx: 22.5, cy: 15, r: 0.25, fill: "var(--color-flame)", stroke: "none" }), _jsxs("g", { fontSize: 1.05, fill: "var(--color-text-secondary)", stroke: "none", children: [_jsx("text", { x: 22.5, y: 7, textAnchor: "middle", children: "110%" }), _jsx("text", { x: 22.5, y: 23.4, textAnchor: "middle", children: "90%" }), _jsx("text", { "data-flame-part": "scale-label", ref: scaleLabel, x: 23.25, y: 15.35, children: "100.0%" })] })] })] })] }));
}
//# sourceMappingURL=flame.js.map